import { ref, computed, watch } from 'vue'
import { driver, type DriveStep, type Config } from 'driver.js'
import 'driver.js/dist/driver.css'

// Types
export interface TourStep extends DriveStep {
  // Extended step options if needed
}

export interface TourConfig {
  id: string
  name: string
  description: string
  steps: TourStep[]
}

// Storage keys
const STORAGE_KEY = 'hris_onboarding_progress'

// Get completed tours from localStorage
const getCompletedTours = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

// Save completed tours to localStorage
const saveCompletedTours = (tours: string[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tours))
}

// Composable
export function useOnboarding() {
  const isRunning = ref(false)
  const currentTourId = ref<string | null>(null)
  const completedTours = ref<string[]>(getCompletedTours())

  // Default driver configuration
  const defaultConfig: Partial<Config> = {
    animate: true,
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    overlayColor: 'rgba(0, 0, 0, 0.7)',
    stagePadding: 10,
    stageRadius: 5,
    popoverClass: 'hris-tour-popover',
    nextBtnText: 'Next →',
    prevBtnText: '← Back',
    doneBtnText: 'Done ✓',
    progressText: '{{current}} of {{total}}',
    onDestroyed: () => {
      isRunning.value = false
      currentTourId.value = null
    },
  }

  // Start a tour
  const startTour = (tourId: string, steps: TourStep[], options?: Partial<Config>) => {
    if (isRunning.value) return

    isRunning.value = true
    currentTourId.value = tourId

    const driverObj = driver({
      ...defaultConfig,
      ...options,
      steps,
      onDestroyStarted: () => {
        // Mark tour as completed when finished
        if (!completedTours.value.includes(tourId)) {
          completedTours.value.push(tourId)
          saveCompletedTours(completedTours.value)
        }
        driverObj.destroy()
      },
    })

    driverObj.drive()
    return driverObj
  }

  // Check if tour was completed
  const isTourCompleted = (tourId: string) => {
    return completedTours.value.includes(tourId)
  }

  // Reset tour progress
  const resetTourProgress = (tourId?: string) => {
    if (tourId) {
      completedTours.value = completedTours.value.filter((id) => id !== tourId)
    } else {
      completedTours.value = []
    }
    saveCompletedTours(completedTours.value)
  }

  // Check if should auto-start tour for first-time users
  const shouldAutoStart = (tourId: string) => {
    return !isTourCompleted(tourId)
  }

  return {
    isRunning: computed(() => isRunning.value),
    currentTourId: computed(() => currentTourId.value),
    completedTours: computed(() => completedTours.value),
    startTour,
    isTourCompleted,
    resetTourProgress,
    shouldAutoStart,
  }
}
